import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Plus, Edit3, X, History } from 'lucide-react';

const EnhancedDashboard = () => {
    // Team Members
    const teamMembers = [
        { id: 1, name: 'Maya Andrews', email: 'mla234@cornell.edu', slack: '@maya.andrews' },
        { id: 2, name: 'Giacomo Arena', email: 'ga358@cornell.edu', slack: '@giacomo.arena' },
        { id: 3, name: 'Rhett Bartolini', email: 'rdb284@cornell.edu', slack: '@rhett.bartolini' },
        { id: 4, name: 'Jiecheng Dong', email: 'jd2334@cornell.edu', slack: '@jiecheng.dong' },
        { id: 5, name: 'Alamin Mohammed', email: 'am3299@cornell.edu', slack: '@alamin.mohammed' }
    ];

    // Change Log State
    const [changeLog, setChangeLog] = useState([
        {
            id: 1,
            timestamp: '2025-06-18T08:00:00Z',
            user: 'Maya Andrews',
            action: 'Created',
            itemType: 'Action Item',
            itemName: 'Configure the slide deck for the Project Charter',
            changes: 'Initial creation with high priority',
            ragBefore: null,
            ragAfter: 'Green'
        },
        {
            id: 2,
            timestamp: '2025-06-19T10:00:00Z',
            user: 'Alamin Mohammed',
            action: 'Created',
            itemType: 'Action Item',
            itemName: 'Complete project scope and profile',
            changes: 'Initial creation with high priority',
            ragBefore: null,
            ragAfter: 'Green'
        },
        {
            id: 3,
            timestamp: '2025-06-20T13:30:00Z',
            user: 'Rhett Bartolini',
            action: 'Created',
            itemType: 'Action Item',
            itemName: 'Quality check and make changes if necessary',
            changes: 'Assigned task for quality assurance with deadline 06/22',
            ragBefore: null,
            ragAfter: 'Green'
        },
        {
            id: 4,
            timestamp: '2025-06-20T15:00:00Z',
            user: 'Rhett Bartolini',
            action: 'Updated',
            itemType: 'Action Item',
            itemName: 'Look over project scope and add/modify details',
            changes: 'Updated notes and marked as complete',
            ragBefore: 'Green',
            ragAfter: 'Green'
        },
        {
            id: 5,
            timestamp: '2025-06-21T09:00:00Z',
            user: 'Giacomo Arena',
            action: 'Created',
            itemType: 'Action Item',
            itemName: 'Update profile and Take on Key Questions based on Team',
            changes: 'Created with medium priority',
            ragBefore: null,
            ragAfter: 'Green'
        },
        {
            id: 6,
            timestamp: '2025-06-21T14:30:00Z',
            user: 'Jiecheng Dong',
            action: 'Created',
            itemType: 'Action Item',
            itemName: 'Update profile overseas any ways of optimization',
            changes: 'New task created for profile optimization analysis',
            ragBefore: null,
            ragAfter: 'Amber'
        }
    ]);

    // Project Status
    const [projectStatus, setProjectStatus] = useState({
        overall: 'Green',
        lastPeriod: 'Green',
        currentWeek: 'Week 6',
        reportDate: new Date().toLocaleDateString()
    });

    // Outstanding Action Items - Using React state with localStorage persistence
    const [actionItems, setActionItems] = useState(() => {
        const DATA_VERSION = '2.2'; // Increment this to force refresh of default data
        
        try {
            const savedItems = localStorage.getItem('dashboardActionItems');
            const savedVersion = localStorage.getItem('dashboardDataVersion');
            
            // If version doesn't match or no version exists, use new default data
            if (savedItems && savedVersion === DATA_VERSION) {
                return JSON.parse(savedItems);
            }
        } catch (error) {
            console.error('Error loading saved action items:', error);
        }
        
        // Set new version and return default action items for the completed project
        localStorage.setItem('dashboardDataVersion', DATA_VERSION);
        
        const defaultItems = [
            {
                id: 1,
                action: 'Stakeholder communication & alignment',
                description: 'Coordinate communication between internal team members and external stakeholders, including project coach, professors, and SC Johnson leadership, to ensure alignment on scope, expectations, and deliverables.',
                owner: 3, // Rhett Bartolini
                dueDate: '2025-06-25',
                completedDate: '2025-06-25',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Kickoff alignment completed. Ongoing updates shared with faculty and leadership throughout early project stages.',
                phase: 'Project Foundation & Stakeholder Alignment'
            },
            {
                id: 2,
                action: 'Final scope confirmation & documentation review',
                description: 'Validate final project scope, assumptions, and deliverables before execution begins. Ensure documentation reflects agreed-upon objectives.',
                owner: 3, // Rhett Bartolini
                dueDate: '2025-06-25',
                completedDate: '2025-06-25',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Scope finalized and approved prior to data exploration phase.',
                phase: 'Project Foundation & Stakeholder Alignment'
            },
            {
                id: 3,
                action: 'Data cleaning & preprocessing',
                description: 'Clean, normalize, and preprocess startup, IPO, and market datasets to prepare for modeling and analysis.',
                owner: 1, // Maya Andrews
                dueDate: '2025-07-08',
                completedDate: '2025-07-10',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Red',
                notes: 'Missing values handled, outliers reviewed, datasets standardized across sources. Completed 2 days behind schedule due to competing final semester project deadlines.',
                phase: 'Data Cleaning, Exploration & Market Research'
            },
            {
                id: 4,
                action: 'IPO research & exploratory data analysis',
                description: 'Research historical IPO performance and conduct exploratory analysis to identify trends, drivers, and relevant indicators.',
                owner: 1, // Maya Andrews
                dueDate: '2025-07-10',
                completedDate: '2025-07-10',
                priority: 'Medium',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Findings informed feature selection and downstream modeling.',
                phase: 'Data Cleaning, Exploration & Market Research'
            },
            {
                id: 5,
                action: 'Initial model experimentation',
                description: 'Develop and test early-stage models to assess feasibility and signal strength within cleaned datasets.',
                owner: 1, // Maya Andrews
                dueDate: '2025-07-12',
                completedDate: '2025-07-12',
                priority: 'Medium',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Results used to refine modeling approach.',
                phase: 'Data Cleaning, Exploration & Market Research'
            },
            {
                id: 6,
                action: 'ROI calculator development using NLP signals',
                description: 'Design and implement an ROI calculator leveraging NLP-derived signals and startup-level indicators to estimate investment potential.',
                owner: 4, // Jiecheng Dong
                dueDate: '2025-07-23',
                completedDate: '2025-07-23',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Integrated textual sentiment and qualitative indicators into structured ROI logic.',
                phase: 'Predictive Modeling & ROI Analysis'
            },
            {
                id: 7,
                action: 'NLP feature extraction & validation',
                description: 'Extract relevant NLP features from startup-related text data and validate their contribution to ROI predictions.',
                owner: 4, // Jiecheng Dong
                dueDate: '2025-07-25',
                completedDate: '2025-07-25',
                priority: 'Medium',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Features evaluated for consistency and interpretability.',
                phase: 'Predictive Modeling & ROI Analysis'
            },
            {
                id: 8,
                action: 'Python-based analytics & seasonality analysis',
                description: 'Perform advanced Python analysis, including seasonality and operational trend evaluation, to support investment decision-making.',
                owner: 5, // Alamin Mohammed
                dueDate: '2025-08-03',
                completedDate: '2025-08-03',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Insights used to contextualize model outputs and strategy recommendations.',
                phase: 'Advanced Analytics, Operations & Dashboard Development'
            },
            {
                id: 9,
                action: 'KPI definition & operational metrics design',
                description: 'Define firm-level KPIs aligned with investment operations, performance tracking, and execution monitoring.',
                owner: 5, // Alamin Mohammed
                dueDate: '2025-08-05',
                completedDate: '2025-08-05',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'KPIs mapped directly to dashboard components.',
                phase: 'Advanced Analytics, Operations & Dashboard Development'
            },
            {
                id: 10,
                action: 'KPI dashboard development',
                description: 'Build an interactive KPI dashboard using HTML, CSS, and JavaScript to visualize project progress, performance metrics, and risk indicators.',
                owner: 5, // Alamin Mohammed
                dueDate: '2025-08-05',
                completedDate: '2025-08-05',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Dashboard supports dynamic updates and leadership-level review.',
                phase: 'Advanced Analytics, Operations & Dashboard Development'
            },
            {
                id: 11,
                action: 'External data benchmarking & comparison',
                description: 'Compare startup and IPO data against external sources such as Moody\'s, SEC filings, and international market datasets.',
                owner: 2, // Giacomo Arena
                dueDate: '2025-07-15',
                completedDate: '2025-07-15',
                priority: 'Medium',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Identified consistency across regions and validated data reliability.',
                phase: 'External Data Validation & Quality Assurance'
            },
            {
                id: 12,
                action: 'Data quality assurance post-cleaning',
                description: 'Conduct QA checks on cleaned datasets to ensure accuracy, completeness, and alignment with modeling requirements.',
                owner: 2, // Giacomo Arena
                dueDate: '2025-07-28',
                completedDate: '2025-07-28',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Issues resolved prior to final model execution.',
                phase: 'External Data Validation & Quality Assurance'
            },
            {
                id: 13,
                action: 'Dashboard QA & assessment alignment',
                description: 'Review final dashboard outputs to ensure analytical results align with assessment criteria and project goals.',
                owner: 2, // Giacomo Arena
                dueDate: '2025-08-08',
                completedDate: '2025-08-08',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Green',
                notes: 'Approved for final submission.',
                phase: 'External Data Validation & Quality Assurance'
            },
            {
                id: 14,
                action: 'Cross-functional final review',
                description: 'Coordinate final cross-functional review of deliverables prior to submission and presentation.',
                owner: 3, // Rhett Bartolini
                dueDate: '2025-08-10',
                completedDate: '2025-08-12',
                priority: 'High',
                status: 'Complete',
                ragStatus: 'Red',
                notes: 'All sections validated and approved by team. Completed 2 days behind schedule due to C-suite executive coach availability constraints during final semester.',
                phase: 'Final Review & Submission Readiness'
            }
        ];
        
        // Save the new default items to localStorage
        localStorage.setItem('dashboardActionItems', JSON.stringify(defaultItems));
        return defaultItems;
    });

    useEffect(() => {
        try {
            localStorage.setItem('dashboardActionItems', JSON.stringify(actionItems));
        } catch (error) {
            console.error('Error saving action items:', error);
        }
    }, [actionItems]);


    // Milestones - Using React state with localStorage persistence
    const [milestones, setMilestones] = useState(() => {
        try {
            const savedMilestones = localStorage.getItem('dashboardMilestones');
            if (savedMilestones) {
                return JSON.parse(savedMilestones);
            }
        } catch (error) {
            console.error('Error loading saved milestones:', error);
        }

        // Default milestones if none saved or error loading
        return [
            {
                id: 1,
                name: 'Phase 1: Project Foundation',
                plannedDate: '06/25/2025',
                actualDate: '06/25/2025',
                status: 'Complete',
                ragStatus: 'Green',
                description: 'Project Charter & Dashboard, Team Structure & Roles, Scope Definition, Stakeholder Alignment'
            },
            {
                id: 2,
                name: 'Phase 2: Data Exploration & Market Analysis',
                plannedDate: '07/10/2025',
                actualDate: '07/10/2025',
                status: 'Complete',
                ragStatus: 'Green',
                description: 'Market Landscape Report, Startup Database Analysis, Funding Trend Analysis, Competitive Intelligence'
            },
            {
                id: 3,
                name: 'Phase 3: Predictive Model Development',
                plannedDate: '07/25/2025',
                actualDate: '07/25/2025',
                status: 'Complete',
                ragStatus: 'Green',
                description: 'ML Model Architecture, Success Prediction Algorithm, Risk Assessment Framework, Validation & Testing'
            },
            {
                id: 4,
                name: 'Phase 4: Strategic Insight Development',
                plannedDate: '08/05/2025',
                actualDate: '08/05/2025',
                status: 'Complete',
                ragStatus: 'Green',
                description: 'Investment Framework, Market Opportunity Map, Portfolio Recommendations, Strategic Guidelines'
            },
            {
                id: 5,
                name: 'Phase 5: Final Presentation & Handover',
                plannedDate: '08/10/2025',
                actualDate: '08/10/2025',
                status: 'Complete',
                ragStatus: 'Green',
                description: 'Executive Presentation, Technical Documentation, Implementation Roadmap, Knowledge Transfer'
            }
        ];
    });

    // Calculate dynamic KPI metrics based on actual task data
    const kpiMetrics = useMemo(() => {
        const totalTasks = actionItems.length;
        const completedTasks = actionItems.filter(item => item.status === 'Complete').length;
        const overdueTasks = actionItems.filter(item => {
            const dueDate = new Date(item.dueDate);
            const today = new Date();
            return item.status !== 'Complete' && dueDate < today;
        }).length;

        // Delivery Rate: Percentage of tasks completed on time
        const onTimeTasks = actionItems.filter(item =>
            item.completedDate && new Date(item.completedDate) <= new Date(item.dueDate)
        ).length;
        const deliveryRate = totalTasks > 0 ? Math.round((onTimeTasks / totalTasks) * 100) : 100;
        const deliveryTarget = 90;
        const deliveryStatus = deliveryRate >= deliveryTarget ? 'Green' : deliveryRate >= deliveryTarget - 10 ? 'Amber' : 'Red';

        // Team Engagement: Percentage of team members actively participating
        const activeMemberIds = [...new Set(actionItems.map(item => item.owner))];
        const teamEngagement = Math.round((activeMemberIds.length / teamMembers.length) * 100);
        const engagementTarget = 100;
        const engagementStatus = teamEngagement >= engagementTarget ? 'Green' : teamEngagement >= engagementTarget - 10 ? 'Amber' : 'Red';

        // Quality Score: Based on tasks with no rework/changes needed
        const highQualityTasks = actionItems.filter(item =>
            item.status === 'Complete' && !item.notes.toLowerCase().includes('rework') && !item.notes.toLowerCase().includes('revision')
        ).length;
        const qualityScore = completedTasks > 0 ? Math.round((highQualityTasks / completedTasks) * 100) : 100;
        const qualityTarget = 95;
        const qualityStatus = qualityScore >= qualityTarget ? 'Green' : qualityScore >= qualityTarget - 5 ? 'Amber' : 'Red';

        // Team Utilization: For completed project, show 100% utilization
        const teamUtilization = 100;
        const utilizationTarget = 80;
        const utilizationStatus = 'Green';

        // Positive trends for completed project
        const deliveryTrend = deliveryRate >= 85 ? '+5%' : '-2%';
        const qualityTrend = qualityScore >= 90 ? '+3%' : '-1%';
        const utilizationTrend = '+10%';
        const engagementTrend = teamEngagement >= 90 ? '+8%' : '-3%';

        return [
            {
                id: 1,
                name: 'Delivery Rate',
                current: deliveryRate,
                target: deliveryTarget,
                status: deliveryStatus,
                trend: deliveryTrend
            },
            {
                id: 2,
                name: 'Quality Score',
                current: qualityScore,
                target: qualityTarget,
                status: qualityStatus,
                trend: qualityTrend
            },
            {
                id: 3,
                name: 'Team Utilization',
                current: teamUtilization,
                target: utilizationTarget,
                status: utilizationStatus,
                trend: utilizationTrend
            },
            {
                id: 4,
                name: 'Team Engagement',
                current: teamEngagement,
                target: engagementTarget,
                status: engagementStatus,
                trend: engagementTrend
            }
        ];
    }, [actionItems, teamMembers.length]);

    // Modal States
    const [showActionModal, setShowActionModal] = useState(false);
    const [showChangeLogModal, setShowChangeLogModal] = useState(false);
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [editingAction, setEditingAction] = useState(null);
    const [editingMilestone, setEditingMilestone] = useState(null);

    // Form State
    const [newAction, setNewAction] = useState({
        action: '',
        owner: 1,
        dueDate: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
        description: '',
        notes: '',
        priority: 'Medium',
        ragStatus: 'Green'
    });

    const [newMilestone, setNewMilestone] = useState({
        name: '',
        plannedDate: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
        actualDate: '',
        status: 'Not Started',
        ragStatus: 'Green',
        description: ''
    });

    // Change Tracking
    const addToChangeLog = (change) => {
        const newLogEntry = {
            id: changeLog.length + 1,
            timestamp: new Date().toISOString(),
            ...change
        };
        setChangeLog(prev => [newLogEntry, ...prev]);
    };

    // Update Action Item with change tracking
    const updateActionItem = (id, updates) => {
        const oldItem = actionItems.find(item => item.id === id);

        const updatedItems = actionItems.map(item =>
            item.id === id ? {
                ...item,
                ...updates,
                lastModified: new Date().toISOString()
            } : item
        );

        setActionItems(updatedItems);

        // Log the change
        if (oldItem) {
            const changes = Object.keys(updates).map(key => {
                if (key === 'status') {
                    return `Status: ${oldItem[key]} → ${updates[key]}`;
                } else if (key === 'ragStatus') {
                    return `RAG: ${oldItem[key]} → ${updates[key]}`;
                } else {
                    return `${key}: ${oldItem[key]} → ${updates[key]}`;
                }
            }).join(', ');

            addToChangeLog({
                user: 'Current User', // In real app, get from auth
                action: 'Updated',
                itemType: 'Action Item',
                itemName: oldItem.action,
                changes: changes,
                ragBefore: oldItem.ragStatus,
                ragAfter: updates.ragStatus || oldItem.ragStatus
            });
        }
    };

    // Toggle Action Completion
    const toggleActionCompletion = (id) => {
        const item = actionItems.find(a => a.id === id);
        const isCompleting = item.status !== 'Complete';

        updateActionItem(id, {
            status: isCompleting ? 'Complete' : 'In Progress',
            completedDate: isCompleting ? new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : null,
            ragStatus: isCompleting ? 'Green' : item.ragStatus
        });
    };

    // Add New Action
    const addNewAction = () => {
        let updatedItems;

        if (editingAction) {
            // Update existing action
            updatedItems = actionItems.map(item =>
                item.id === editingAction.id ? {
                    ...newAction,
                    id: editingAction.id,
                    lastModified: new Date().toISOString(),
                    createdDate: editingAction.createdDate,
                    status: editingAction.status,
                    completedDate: editingAction.completedDate,
                    reminderSent: editingAction.reminderSent
                } : item
            );

            addToChangeLog({
                user: 'Current User',
                action: 'Updated',
                itemType: 'Action Item',
                itemName: newAction.action,
                changes: `Updated task details`,
                ragBefore: editingAction.ragStatus,
                ragAfter: newAction.ragStatus
            });
        } else {
            // Add new action
            const newId = Math.max(...actionItems.map(a => a.id)) + 1;
            const newItem = {
                ...newAction,
                id: newId,
                status: 'Not Started',
                completedDate: null,
                createdDate: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
                lastModified: new Date().toISOString(),
                reminderSent: false
            };

            updatedItems = [...actionItems, newItem];

            addToChangeLog({
                user: 'Current User',
                action: 'Created',
                itemType: 'Action Item',
                itemName: newAction.action,
                changes: `Created with priority: ${newAction.priority}, assigned to: ${getMemberById(newAction.owner).name}`,
                ragBefore: null,
                ragAfter: newAction.ragStatus
            });
        }

        setActionItems(updatedItems);

        setNewAction({
            action: '',
            owner: 1,
            dueDate: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            description: '',
            notes: '',
            priority: 'Medium',
            ragStatus: 'Green'
        });
        setEditingAction(null);
        setShowActionModal(false);
    };

    // Edit Action
    const editAction = (action) => {
        setEditingAction(action);
        setNewAction({
            action: action.action,
            owner: action.owner,
            dueDate: action.dueDate,
            description: action.description,
            notes: action.notes,
            priority: action.priority,
            ragStatus: action.ragStatus
        });
        setShowActionModal(true);
    };

    // Add/Edit Milestone
    const addNewMilestone = () => {
        if (editingMilestone) {
            // Update existing milestone
            setMilestones(prev => prev.map(item =>
                item.id === editingMilestone.id ? {
                    ...newMilestone,
                    id: editingMilestone.id
                } : item
            ));

            addToChangeLog({
                user: 'Current User',
                action: 'Updated',
                itemType: 'Milestone',
                itemName: newMilestone.name,
                changes: `Updated milestone details`,
                ragBefore: editingMilestone.ragStatus,
                ragAfter: newMilestone.ragStatus
            });
        } else {
            // Add new milestone
            const newId = Math.max(...milestones.map(m => m.id)) + 1;
            setMilestones(prev => [...prev, { ...newMilestone, id: newId }]);

            addToChangeLog({
                user: 'Current User',
                action: 'Created',
                itemType: 'Milestone',
                itemName: newMilestone.name,
                changes: `Created new milestone`,
                ragBefore: null,
                ragAfter: newMilestone.ragStatus
            });
        }

        setNewMilestone({
            name: '',
            plannedDate: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
            actualDate: '',
            status: 'Not Started',
            ragStatus: 'Green',
            description: ''
        });
        setEditingMilestone(null);
        setShowMilestoneModal(false);
    };

    // Edit Milestone
    const editMilestone = (milestone) => {
        setEditingMilestone(milestone);
        setNewMilestone({
            name: milestone.name,
            plannedDate: milestone.plannedDate,
            actualDate: milestone.actualDate || '',
            status: milestone.status,
            ragStatus: milestone.ragStatus,
            description: milestone.description || ''
        });
        setShowMilestoneModal(true);
    };

    // Get Member Name
    const getMemberById = (id) => {
        return teamMembers.find(m => m.id === id) || teamMembers[0];
    };

    // Calculate Performance Metrics
    const getPerformanceData = () => {
        return teamMembers.map(member => {
            const memberActions = actionItems.filter(item => item.owner === member.id);
            const completed = memberActions.filter(item => item.status === 'Complete').length;
            const onTime = memberActions.filter(item =>
                item.completedDate && new Date(item.completedDate) <= new Date(item.dueDate)
            ).length;

            return {
                name: member.name.split(' ')[0],
                assigned: memberActions.length,
                completed: completed,
                onTime: onTime,
                completionRate: memberActions.length > 0 ? Math.round((completed / memberActions.length) * 100) : 100,
                onTimeRate: memberActions.length > 0 ? Math.round((onTime / memberActions.length) * 100) : 100
            };
        });
    };

    // RAG Status Distribution
    const getRagDistribution = () => {
        const ragCounts = { Red: 0, Amber: 0, Green: 0 };
        actionItems.forEach(item => ragCounts[item.ragStatus]++);

        return [
            { name: 'On Track', value: ragCounts.Green, color: '#10b981', status: 'Green' },
            { name: 'At Risk', value: ragCounts.Amber, color: '#f59e0b', status: 'Amber' },
            { name: 'Delayed', value: ragCounts.Red, color: '#ef4444', status: 'Red' }
        ];
    };

    const performanceData = getPerformanceData();
    const ragDistribution = getRagDistribution();

    // RAG Status Colors
    const ragColors = {
        'Red': '#ef4444',
        'Amber': '#f59e0b',
        'Green': '#10b981'
    };

    // Styles
    const styles = {
        dashboard: {
            minHeight: '100vh',
            backgroundColor: '#f8fafc',
            fontFamily: 'Arial, sans-serif'
        },
        header: {
            backgroundColor: 'white',
            borderBottom: '2px solid #e2e8f0',
            padding: '24px 32px'
        },
        headerContent: {
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1fr auto auto auto',
            alignItems: 'center',
            gap: '16px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1e293b',
            margin: 0
        },
        subtitle: {
            fontSize: '14px',
            color: '#64748b',
            marginTop: '4px'
        },
        statusIndicator: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#f1f5f9',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
        },
        statusDot: (status) => ({
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: status === 'Green' ? '#10b981' : status === 'Amber' ? '#f59e0b' : '#ef4444'
        }),
        iconButton: {
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            backgroundColor: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#64748b'
        },
        addButton: {
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '600'
        },
        main: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '32px',
            display: 'grid',
            gap: '24px'
        },
        section: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '20px',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '8px'
        },
        grid2: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px'
        },
        grid4: {
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px'
        },
        kpiCard: {
            padding: '20px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            textAlign: 'center'
        },
        kpiValue: {
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#1e293b'
        },
        kpiLabel: {
            fontSize: '14px',
            color: '#64748b',
            marginTop: '4px'
        },
        kpiTarget: {
            fontSize: '12px',
            color: '#94a3b8',
            marginTop: '8px'
        },
        actionTable: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px'
        },
        tableHeader: {
            backgroundColor: '#f8fafc',
            borderBottom: '2px solid #e2e8f0'
        },
        tableHeaderCell: {
            padding: '12px 16px',
            textAlign: 'left',
            fontWeight: '600',
            color: '#374151'
        },
        tableRow: {
            borderBottom: '1px solid #f1f5f9'
        },
        tableCell: {
            padding: '12px 16px',
            verticalAlign: 'top'
        },
        ragBadge: (status) => ({
            display: 'inline-block',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: ragColors[status]
        }),
        priorityBadge: (priority) => ({
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: priority === 'High' ? '#fee2e2' : priority === 'Medium' ? '#fef3c7' : '#f0f9ff',
            color: priority === 'High' ? '#dc2626' : priority === 'Medium' ? '#d97706' : '#2563eb'
        }),
        modal: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modalContent: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
        },
        formGroup: {
            marginBottom: '20px'
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '6px'
        },
        input: {
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
        },
        textarea: {
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            minHeight: '80px',
            resize: 'vertical'
        },
        select: {
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            backgroundColor: 'white'
        },
        changeLogItem: {
            padding: '12px',
            borderLeft: '4px solid #e2e8f0',
            marginBottom: '12px',
            backgroundColor: '#f8fafc'
        },
        timestamp: {
            fontSize: '12px',
            color: '#64748b',
            marginBottom: '4px'
        },
        roadmapCard: {
            backgroundColor: '#fafbfc',
            border: '2px solid #e5e9ed',
            borderRadius: '16px',
            padding: '28px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease'
        }
    };

    return (
        <div style={styles.dashboard}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerContent}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {/* Big Red Capital Logo */}
                        <img
                            src="/Logo.png"
                            alt="Big Red Capital Logo"
                            style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                border: '3px solid #e2e8f0',
                                backgroundColor: 'white',
                                padding: '6px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                            }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div>
                            <h1 style={styles.title}>Big Red Capital: Startup Analysis Project</h1>
                            <p style={styles.subtitle}>Team 17 Balanced Scorecard Dashboard - Status Report: {projectStatus.reportDate}</p>
                        </div>
                    </div>

                    <div style={styles.statusIndicator}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Overall Status:</span>
                        <div style={styles.statusDot(projectStatus.overall)}></div>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{projectStatus.overall}</span>
                    </div>

                    <button
                        style={styles.iconButton}
                        onClick={() => setShowChangeLogModal(true)}
                        title="View Change Log"
                    >
                        <History size={16} />
                        <span>Changes</span>
                    </button>

                    <button
                        style={styles.addButton}
                        onClick={() => setShowActionModal(true)}
                    >
                        <Plus size={16} />
                        Add Action Item
                    </button>
                </div>
            </div>

            <div style={styles.main}>
                {/* KPI Dashboard */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>Key Performance Indicators</h2>
                    <div style={styles.grid4}>
                        {kpiMetrics.map(kpi => (
                            <div
                                key={kpi.id}
                                style={{
                                    ...styles.kpiCard,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                }}
                            >
                                {/* Animated background indicator */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '4px',
                                    backgroundColor: ragColors[kpi.status],
                                    borderRadius: '8px 8px 0 0'
                                }}></div>

                                <div style={{
                                    ...styles.ragBadge(kpi.status),
                                    position: 'absolute',
                                    top: '12px',
                                    right: '12px',
                                    width: '16px',
                                    height: '16px',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                }}></div>

                                <div style={{
                                    ...styles.kpiValue,
                                    color: '#1e293b',
                                    fontWeight: '900',
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                                }}>
                                    {kpi.current}%
                                </div>

                                <div style={{
                                    ...styles.kpiLabel,
                                    color: '#1e293b',
                                    fontWeight: '600',
                                    fontSize: '16px'
                                }}>
                                    {kpi.name}
                                </div>

                                <div style={{
                                    ...styles.kpiTarget,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    color: '#374151',
                                    fontWeight: '600',
                                    fontSize: '14px'
                                }}>
                                    <span>Target: {kpi.target}%</span>
                                    <span style={{
                                        color: kpi.trend.includes('+') ? '#059669' : '#dc2626',
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        backgroundColor: kpi.trend.includes('+') ? '#ecfdf5' : '#fef2f2',
                                        padding: '3px 8px',
                                        borderRadius: '12px',
                                        border: `1px solid ${kpi.trend.includes('+') ? '#059669' : '#dc2626'}`
                                    }}>
                                        {kpi.trend}
                                    </span>
                                </div>

                                {/* Progress bar */}
                                <div style={{
                                    marginTop: '12px',
                                    width: '100%',
                                    height: '6px',
                                    backgroundColor: '#e2e8f0',
                                    borderRadius: '3px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%',
                                        width: `${Math.min((kpi.current / kpi.target) * 100, 100)}%`,
                                        backgroundColor: ragColors[kpi.status],
                                        borderRadius: '3px',
                                        transition: 'width 1s ease-in-out'
                                    }}></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Real-time KPI Summary */}
                    <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#f1f5f9',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}>
                        <div style={{ fontSize: '16px', color: '#1e293b', fontWeight: '600' }}>
                            Performance Overview: {kpiMetrics.filter(k => k.status === 'Green').length} On Track |{' '}
                            {kpiMetrics.filter(k => k.status === 'Amber').length} At Risk |{' '}
                            {kpiMetrics.filter(k => k.status === 'Red').length} Delayed
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#64748b',
                            fontWeight: '500'
                        }}>
                            Updates automatically as tasks are completed
                        </div>
                    </div>
                </div>

                {/* Performance Charts */}
                <div style={styles.grid2}>
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>Team Performance Metrics</h2>
                        <div style={{ height: '400px', width: '100%', padding: '10px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={performanceData}
                                    margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
                                >
                                    <defs>
                                        <linearGradient id="completionGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.6}/>
                                        </linearGradient>
                                        <linearGradient id="onTimeGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.6}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 14, fill: '#1e293b', fontWeight: '600' }}
                                        axisLine={{ stroke: '#e2e8f0' }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 14, fill: '#1e293b', fontWeight: '600' }}
                                        axisLine={{ stroke: '#e2e8f0' }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '2px solid #1e293b',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: '#1e293b'
                                        }}
                                        formatter={(value, name) => [
                                            `${value}%`,
                                            name === 'completionRate' ? 'Completion Rate' : 'On-Time Rate'
                                        ]}
                                    />
                                    <Bar
                                        dataKey="completionRate"
                                        fill="url(#completionGradient)"
                                        name="Completion Rate"
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="onTimeRate"
                                        fill="url(#onTimeGradient)"
                                        name="On-Time Rate"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Real-time Performance Summary */}
                        <div style={{
                            marginTop: '10px',
                            padding: '16px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <div style={{ fontSize: '16px', color: '#1e293b', marginBottom: '8px', fontWeight: '700' }}>
                                Team Summary: Total Tasks: {performanceData.reduce((sum, member) => sum + member.assigned, 0)} |{' '}
                                Completed: {performanceData.reduce((sum, member) => sum + member.completed, 0)} |{' '}
                                On-Time Rate: {Math.round((actionItems.filter(item => 
                                    item.completedDate && new Date(item.completedDate) <= new Date(item.dueDate)
                                ).length / Math.max(actionItems.length, 1)) * 100)}%
                            </div>
                            <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', fontStyle: 'italic' }}>
                                Minor delays occurred due to competing final semester project deadlines and C-suite executive coach scheduling constraints. All deliverables completed successfully.
                            </div>
                        </div>
                    </div>

                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>RAG Status Distribution</h2>
                        <ResponsiveContainer width="100%" height={450}>
                            <PieChart>
                                <defs>
                                    <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#22c55e" />
                                        <stop offset="100%" stopColor="#16a34a" />
                                    </linearGradient>
                                    <linearGradient id="amberGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#f59e0b" />
                                        <stop offset="100%" stopColor="#d97706" />
                                    </linearGradient>
                                    <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#ef4444" />
                                        <stop offset="100%" stopColor="#dc2626" />
                                    </linearGradient>
                                </defs>
                                <Pie
                                    data={ragDistribution}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={85}
                                    outerRadius={150}
                                    paddingAngle={2}
                                    stroke="#ffffff"
                                    strokeWidth={4}
                                    label={({ name, value, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                                        const RADIAN = Math.PI / 180;
                                        const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                        return (
                                            <g>
                                                <line
                                                    x1={cx + (innerRadius + (outerRadius - innerRadius) * 1.1) * Math.cos(-midAngle * RADIAN)}
                                                    y1={cy + (innerRadius + (outerRadius - innerRadius) * 1.1) * Math.sin(-midAngle * RADIAN)}
                                                    x2={x}
                                                    y2={y}
                                                    stroke="#94a3b8"
                                                    strokeWidth={2}
                                                />
                                                <text
                                                    x={x}
                                                    y={y - 8}
                                                    textAnchor="middle"
                                                    dominantBaseline="central"
                                                    fontSize={16}
                                                    fontWeight="900"
                                                    fill="#1e293b"
                                                    stroke="white"
                                                    strokeWidth={3}
                                                    paintOrder="stroke"
                                                >
                                                    {name}
                                                </text>
                                                <text
                                                    x={x}
                                                    y={y + 12}
                                                    textAnchor="middle"
                                                    dominantBaseline="central"
                                                    fontSize={14}
                                                    fontWeight="800"
                                                    fill="#1e293b"
                                                    stroke="white"
                                                    strokeWidth={3}
                                                    paintOrder="stroke"
                                                >
                                                    {value} ({(percent * 100).toFixed(0)}%)
                                                </text>
                                            </g>
                                        );
                                    }}
                                    labelLine={false}
                                >
                                    {ragDistribution.map((entry, index) => {
                                        const gradientMap = {
                                            'On Track': 'url(#greenGradient)',
                                            'At Risk': 'url(#amberGradient)',
                                            'Delayed': 'url(#redGradient)'
                                        };

                                        return (
                                            <Cell
                                                key={index}
                                                fill={gradientMap[entry.name] || entry.color}
                                            />
                                        );
                                    })}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '2px solid #1e293b',
                                        borderRadius: '8px',
                                        color: '#1e293b',
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                        padding: '12px 16px'
                                    }}
                                    formatter={(value, name) => [`${value} items`, `${name}`]}
                                />
                            </PieChart>
                        </ResponsiveContainer>

                        {/* Enhanced Status Summary */}
                        <div style={{
                            marginTop: '20px',
                            padding: '16px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '12px',
                            border: '2px solid #e2e8f0'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '40px',
                                flexWrap: 'wrap'
                            }}>
                                {ragDistribution.map((entry, index) => (
                                    <div key={index} style={{
                                        textAlign: 'center',
                                        padding: '12px 20px',
                                        backgroundColor: 'white',
                                        borderRadius: '10px',
                                        border: `2px solid ${entry.color}`,
                                        minWidth: '100px',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <div style={{
                                            fontSize: '24px',
                                            fontWeight: '900',
                                            color: entry.color,
                                            marginBottom: '4px'
                                        }}>
                                            {entry.value}
                                        </div>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '2px'
                                        }}>
                                            {entry.name}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#64748b',
                                            fontWeight: '600'
                                        }}>
                                            {((entry.value / ragDistribution.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}%
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Outstanding Action Items */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>Action Items</h2>
                    <table style={styles.actionTable}>
                        <thead style={styles.tableHeader}>
                        <tr>
                            <th style={styles.tableHeaderCell}>RAG</th>
                            <th style={styles.tableHeaderCell}>Action</th>
                            <th style={styles.tableHeaderCell}>Owner</th>
                            <th style={styles.tableHeaderCell}>Due Date</th>
                            <th style={styles.tableHeaderCell}>Priority</th>
                            <th style={styles.tableHeaderCell}>Status</th>
                            <th style={styles.tableHeaderCell}>Description & Notes</th>
                            <th style={styles.tableHeaderCell}>Complete</th>
                            <th style={styles.tableHeaderCell}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {actionItems.map(item => (
                            <tr key={item.id} style={styles.tableRow}>
                                <td style={styles.tableCell}>
                                    <div style={styles.ragBadge(item.ragStatus)}></div>
                                </td>
                                <td style={{ ...styles.tableCell, fontWeight: '600' }}>
                                    {item.action}
                                </td>
                                <td style={styles.tableCell}>
                                    {getMemberById(item.owner).name}
                                </td>
                                <td style={styles.tableCell}>
                                    {item.dueDate}
                                </td>
                                <td style={styles.tableCell}>
                                    <span style={styles.priorityBadge(item.priority)}>
                                        {item.priority}
                                    </span>
                                </td>
                                <td style={styles.tableCell}>
                                    {item.status}
                                </td>
                                <td style={styles.tableCell}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <strong>Description:</strong> {item.description}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                                        <strong>Notes:</strong> {item.notes}
                                    </div>
                                </td>
                                <td style={styles.tableCell}>
                                    <input
                                        type="checkbox"
                                        checked={item.status === 'Complete'}
                                        onChange={() => toggleActionCompletion(item.id)}
                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                    />
                                </td>
                                <td style={styles.tableCell}>
                                    <button
                                        onClick={() => editAction(item)}
                                        style={{
                                            padding: '4px 8px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <Edit3 size={12} />
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {/* Project Milestones Table */}
                <div style={styles.section}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={styles.sectionTitle}>Milestone Management Table</h2>
                    </div>
                    <table style={styles.actionTable}>
                        <thead style={styles.tableHeader}>
                        <tr>
                            <th style={styles.tableHeaderCell}>Milestone</th>
                            <th style={styles.tableHeaderCell}>Planned Date</th>
                            <th style={styles.tableHeaderCell}>Actual Date</th>
                            <th style={styles.tableHeaderCell}>Status</th>
                            <th style={styles.tableHeaderCell}>RAG</th>
                            <th style={styles.tableHeaderCell}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {milestones.map(milestone => (
                            <tr key={milestone.id} style={styles.tableRow}>
                                <td style={{ ...styles.tableCell, fontWeight: '600' }}>
                                    {milestone.name}
                                </td>
                                <td style={styles.tableCell}>
                                    {milestone.plannedDate}
                                </td>
                                <td style={styles.tableCell}>
                                    {milestone.actualDate || 'TBD'}
                                </td>
                                <td style={styles.tableCell}>
                                    {milestone.status}
                                </td>
                                <td style={styles.tableCell}>
                                    <div style={styles.ragBadge(milestone.ragStatus)}></div>
                                </td>
                                <td style={styles.tableCell}>
                                    <button
                                        onClick={() => editMilestone(milestone)}
                                        style={{
                                            padding: '4px 8px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <Edit3 size={12} />
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {/* Project Roadmap Timeline */}
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>Project Roadmap & Timeline</h2>

                    {/* Timeline Overview */}
                    <div style={{
                        backgroundColor: '#f1f5f9',
                        borderRadius: '12px',
                        padding: '10px',
                        marginBottom: '32px',
                        border: '2px solid #cbd5e1',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '40px'
                        }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                                Project Timeline Overview
                            </h3>
                            <div style={{
                                display: 'flex',
                                gap: '16px',
                                alignItems: 'center'
                            }}>
                                <div style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                                    Duration: 8 weeks
                                </div>
                                <div style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
                                    June 15 - August 10, 2025
                                </div>
                            </div>
                        </div>

                        {/* Timeline Progress Bar */}
                        <div style={{
                            minWidth: '900px',
                            position: 'relative',
                            height: '160px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            overflowX: 'auto',
                        }}>
                            {/* Main timeline line */}
                            <div style={{
                                position: 'relative',
                                top: '60px',
                                left: '20px',
                                right: 'px',
                                height: '120px',
                                backgroundColor: '#e2e8f0',
                                borderRadius: '2px'
                            }}></div>

                            {/* Progress line */}
                            <div style={{
                                position: 'absolute',
                                top: '60px',
                                left: '20px',
                                width: '80%',
                                height: '4px',
                                backgroundColor: '#10b981',
                                borderRadius: '2px'
                            }}></div>

                            {/* Timeline markers */}
                            {milestones.map((milestone, index) => {
                                const position = 20 + (index * ((100 - 40) / (milestones.length - 1)));
                                const isComplete = milestone.status === 'Complete';
                                const isCurrent = milestone.status === 'In Progress';

                                return (
                                    <div key={milestone.id} style={{
                                        position: 'absolute',
                                        left: `${position}%`,
                                        top: '50px',
                                        transform: 'translateX(-50%)'
                                    }}>
                                        {/* Milestone marker */}
                                        <div style={{
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '50%',
                                            backgroundColor: isComplete ? '#10b981' : isCurrent ? '#f59e0b' : '#e2e8f0',
                                            border: `3px solid ${isComplete ? '#059669' : isCurrent ? '#d97706' : '#cbd5e1'}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '14px',
                                            fontWeight: '900',
                                            color: isComplete || isCurrent ? 'white' : '#64748b',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                        }}>
                                            {isComplete ? '✓' : isCurrent ? '●' : index + 1}
                                        </div>

                                        {/* Milestone label */}
                                        <div style={{
                                            position: 'absolute',
                                            top: index % 2 === 0 ? '-50px' : '40px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            textAlign: 'center',
                                            width: '120px'
                                        }}>
                                            <div style={{
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                color: '#1e293b',
                                                marginBottom: '2px',
                                                lineHeight: '1.2'
                                            }}>
                                                {milestone.name.replace('Phase ', 'P')}
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: '#64748b',
                                                fontWeight: '600'
                                            }}>
                                                {milestone.plannedDate}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Current Status */}
                        <div style={{
                            marginTop: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '16px'
                        }}>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#1e293b'
                            }}>
                                Current Status: Project Complete - All Phases Delivered Successfully
                            </div>
                            <div style={{
                                backgroundColor: '#10b981',
                                color: 'white',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                fontSize: '14px',
                                fontWeight: '700'
                            }}>
                                100% Complete
                            </div>
                        </div>
                    </div>

                    {/* Detailed Phase Breakdown */}
                    <div style={{
                        display: 'grid',
                        gap: '20px'
                    }}>
                        {milestones.map((milestone, index) => {
                            const isComplete = milestone.status === 'Complete';
                            const isCurrent = milestone.status === 'In Progress';

                            // Mock deliverables for each phase
                            const deliverables = {
                                1: ['Project Charter Document', 'Team Dashboard Setup', 'Stakeholder Alignment Report', 'Initial Scope Definition'],
                                2: ['Market Landscape Analysis', 'Startup Database Collection', 'Funding Trend Report', 'Competitive Intelligence Brief'],
                                3: ['ML Model Architecture', 'Success Prediction Algorithm', 'Risk Assessment Framework', 'Model Validation Results'],
                                4: ['Investment Framework', 'Market Opportunity Map', 'Portfolio Recommendations', 'Strategic Guidelines Document'],
                                5: ['Executive Presentation', 'Technical Documentation', 'Implementation Roadmap', 'Knowledge Transfer Session']
                            };

                            return (
                                <div key={milestone.id} style={{
                                    ...styles.roadmapCard,
                                    backgroundColor: isComplete ? '#f0fdf4' : isCurrent ? '#fef3c7' : '#fafbfc',
                                    border: `2px solid ${isComplete ? '#10b981' : isCurrent ? '#f59e0b' : '#e5e9ed'}`,
                                    borderRadius: '16px',
                                    padding: '28px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)'
                                }}>
                                    {/* Phase header */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: '16px'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                marginBottom: '8px'
                                            }}>
                                                <div style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isComplete ? '#10b981' : isCurrent ? '#f59e0b' : '#e2e8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '18px',
                                                    fontWeight: '900',
                                                    color: isComplete || isCurrent ? 'white' : '#64748b'
                                                }}>
                                                    {isComplete ? '✓' : isCurrent ? '●' : index + 1}
                                                </div>
                                                <h3 style={{
                                                    fontSize: '20px',
                                                    fontWeight: '700',
                                                    color: '#1e293b',
                                                    margin: 0
                                                }}>
                                                    {milestone.name}
                                                </h3>
                                            </div>
                                            <p style={{
                                                fontSize: '14px',
                                                color: '#64748b',
                                                margin: 0,
                                                lineHeight: '1.5'
                                            }}>
                                                {milestone.description}
                                            </p>
                                        </div>

                                        <div style={{
                                            textAlign: 'right',
                                            minWidth: '120px'
                                        }}>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                color: '#1e293b',
                                                marginBottom: '4px'
                                            }}>
                                                {milestone.plannedDate}
                                            </div>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '4px 12px',
                                                borderRadius: '16px',
                                                backgroundColor: isComplete ? '#dcfce7' : isCurrent ? '#fef3c7' : '#f1f5f9',
                                                color: isComplete ? '#166534' : isCurrent ? '#92400e' : '#475569',
                                                fontSize: '12px',
                                                fontWeight: '700'
                                            }}>
                                                <div style={styles.ragBadge(milestone.ragStatus)}></div>
                                                {milestone.status}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Deliverables */}
                                    <div style={{
                                        marginTop: '20px'
                                    }}>
                                        <h4 style={{
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            marginBottom: '12px'
                                        }}>
                                            Key Deliverables:
                                        </h4>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                            gap: '12px'
                                        }}>
                                            {deliverables[milestone.id]?.map((deliverable, idx) => (
                                                <div key={idx} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 12px',
                                                    backgroundColor: 'white',
                                                    borderRadius: '6px',
                                                    border: '1px solid #e2e8f0',
                                                    fontSize: '14px'
                                                }}>
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '50%',
                                                        backgroundColor: isComplete ? '#10b981' : isCurrent && idx < 2 ? '#f59e0b' : '#e2e8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '10px',
                                                        fontWeight: '900',
                                                        color: isComplete || (isCurrent && idx < 2) ? 'white' : '#64748b'
                                                    }}>
                                                        {isComplete ? '✓' : (isCurrent && idx < 2) ? '●' : '○'}
                                                    </div>
                                                    <span style={{
                                                        fontWeight: '600',
                                                        color: '#374151'
                                                    }}>
                                                        {deliverable}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>



                                    {/* Edit button */}
                                    <button
                                        onClick={() => editMilestone(milestone)}
                                        style={{
                                            marginTop: '8px',
                                            top: '16px',
                                            right: '16px',
                                            padding: '6px 12px',
                                            backgroundColor: '#3b82f6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <Edit3 size={12} />
                                        Edit
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Add Milestone Button */}
                    <div style={{
                        marginTop: '24px',
                        textAlign: 'center'
                    }}>
                        <button
                            onClick={() => {
                                setEditingMilestone(null);
                                setNewMilestone({
                                    name: '',
                                    plannedDate: new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
                                    actualDate: '',
                                    status: 'Not Started',
                                    ragStatus: 'Green',
                                    description: ''
                                });
                                setShowMilestoneModal(true);
                            }}
                            style={{
                                backgroundColor: '#10b981',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <Plus size={16} />
                            Add New Milestone
                        </button>
                    </div>
                </div>
            </div>

            {/* Change Log Modal */}
            {showChangeLogModal && (
                <div style={styles.modal}>
                    <div style={styles.modalContent}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                                Change Log & Audit Trail
                            </h3>
                            <button
                                onClick={() => setShowChangeLogModal(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                            <p style={{ fontSize: '16px', color: '#1e293b', margin: 0, fontWeight: '600' }}>
                                Total Changes: {changeLog.length} entries | Last Update: {changeLog[0]?.timestamp ? new Date(changeLog[0].timestamp).toLocaleString() : 'No changes yet'}
                            </p>
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {changeLog.map(entry => (
                                <div key={entry.id} style={styles.changeLogItem}>
                                    <div style={styles.timestamp}>
                                        {new Date(entry.timestamp).toLocaleString()} - {entry.user}
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                                        {entry.action} {entry.itemType}: {entry.itemName}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                                        {entry.changes}
                                    </div>
                                    {entry.ragBefore && entry.ragAfter && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                            <span>RAG Status:</span>
                                            <div style={styles.ragBadge(entry.ragBefore)}></div>
                                            <span>{entry.ragBefore}</span>
                                            <span>→</span>
                                            <div style={styles.ragBadge(entry.ragAfter)}></div>
                                            <span>{entry.ragAfter}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button
                                onClick={() => {
                                    // Export change log as CSV
                                    const csvContent = "data:text/csv;charset=utf-8," +
                                        "Timestamp,User,Action,Item Type,Item Name,Changes,RAG Before,RAG After\n" +
                                        changeLog.map(entry =>
                                            `"${entry.timestamp}","${entry.user}","${entry.action}","${entry.itemType}","${entry.itemName}","${entry.changes}","${entry.ragBefore || ''}","${entry.ragAfter || ''}"`
                                        ).join('\n');

                                    const link = document.createElement('a');
                                    link.setAttribute('href', encodeURI(csvContent));
                                    link.setAttribute('download', `team17_change_log_${new Date().toISOString().split('T')[0]}.csv`);
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                                style={{
                                    padding: '10px 20px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    cursor: 'pointer',
                                    marginRight: '12px'
                                }}
                            >
                                Export CSV
                            </button>
                            <button
                                onClick={() => setShowChangeLogModal(false)}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Action Modal */}
            {showActionModal && (
                <div style={styles.modal}>
                    <div style={styles.modalContent}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                                {editingAction ? 'Edit Action Item' : 'Add New Action Item'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowActionModal(false);
                                    setEditingAction(null);
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Action Item</label>
                            <input
                                type="text"
                                value={newAction.action}
                                onChange={(e) => setNewAction(prev => ({ ...prev, action: e.target.value }))}
                                style={styles.input}
                                placeholder="Enter action item description"
                            />
                        </div>

                        <div style={styles.grid2}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Assign To</label>
                                <select
                                    value={newAction.owner}
                                    onChange={(e) => setNewAction(prev => ({ ...prev, owner: parseInt(e.target.value) }))}
                                    style={styles.select}
                                >
                                    {teamMembers.map(member => (
                                        <option key={member.id} value={member.id}>{member.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Due Date</label>
                                <input
                                    type="date"
                                    value={newAction.dueDate}
                                    onChange={(e) => setNewAction(prev => ({ ...prev, dueDate: e.target.value }))}
                                    style={styles.input}
                                />
                            </div>
                        </div>

                        <div style={styles.grid2}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Priority</label>
                                <select
                                    value={newAction.priority}
                                    onChange={(e) => setNewAction(prev => ({ ...prev, priority: e.target.value }))}
                                    style={styles.select}
                                >
                                    <option value="High">High</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Low">Low</option>
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>RAG Status</label>
                                <select
                                    value={newAction.ragStatus}
                                    onChange={(e) => setNewAction(prev => ({ ...prev, ragStatus: e.target.value }))}
                                    style={styles.select}
                                >
                                    <option value="Green">Green - On Track</option>
                                    <option value="Amber">Amber - At Risk</option>
                                    <option value="Red">Red - Delayed</option>
                                </select>
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Description</label>
                            <textarea
                                value={newAction.description}
                                onChange={(e) => setNewAction(prev => ({ ...prev, description: e.target.value }))}
                                style={styles.textarea}
                                placeholder="Detailed description of the action item"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Notes</label>
                            <textarea
                                value={newAction.notes}
                                onChange={(e) => setNewAction(prev => ({ ...prev, notes: e.target.value }))}
                                style={styles.textarea}
                                placeholder="Additional notes, dependencies, or context"
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowActionModal(false);
                                    setEditingAction(null);
                                }}
                                style={{
                                    padding: '10px 20px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addNewAction}
                                disabled={!newAction.action || !newAction.description}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    backgroundColor: newAction.action && newAction.description ? '#3b82f6' : '#9ca3af',
                                    color: 'white',
                                    cursor: newAction.action && newAction.description ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {editingAction ? 'Update Action Item' : 'Add Action Item'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Milestone Modal */}
            {showMilestoneModal && (
                <div style={styles.modal}>
                    <div style={styles.modalContent}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                                {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowMilestoneModal(false);
                                    setEditingMilestone(null);
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Milestone Name</label>
                            <input
                                type="text"
                                value={newMilestone.name}
                                onChange={(e) => setNewMilestone(prev => ({ ...prev, name: e.target.value }))}
                                style={styles.input}
                                placeholder="Enter milestone name"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Description</label>
                            <textarea
                                value={newMilestone.description}
                                onChange={(e) => setNewMilestone(prev => ({ ...prev, description: e.target.value }))}
                                style={styles.textarea}
                                placeholder="Enter milestone description"
                            />
                        </div>

                        <div style={styles.grid2}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Planned Date</label>
                                <input
                                    type="text"
                                    value={newMilestone.plannedDate}
                                    onChange={(e) => setNewMilestone(prev => ({ ...prev, plannedDate: e.target.value }))}
                                    style={styles.input}
                                    placeholder="MM/DD/YYYY"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Actual Date</label>
                                <input
                                    type="text"
                                    value={newMilestone.actualDate}
                                    onChange={(e) => setNewMilestone(prev => ({ ...prev, actualDate: e.target.value }))}
                                    style={styles.input}
                                    placeholder="MM/DD/YYYY or leave empty"
                                />
                            </div>
                        </div>

                        <div style={styles.grid2}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Status</label>
                                <select
                                    value={newMilestone.status}
                                    onChange={(e) => setNewMilestone(prev => ({ ...prev, status: e.target.value }))}
                                    style={styles.select}
                                >
                                    <option value="Not Started">Not Started</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Complete">Complete</option>
                                    <option value="Upcoming">Upcoming</option>
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>RAG Status</label>
                                <select
                                    value={newMilestone.ragStatus}
                                    onChange={(e) => setNewMilestone(prev => ({ ...prev, ragStatus: e.target.value }))}
                                    style={styles.select}
                                >
                                    <option value="Green">Green - On Track</option>
                                    <option value="Amber">Amber - At Risk</option>
                                    <option value="Red">Red - Delayed</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowMilestoneModal(false);
                                    setEditingMilestone(null);
                                }}
                                style={{
                                    padding: '10px 20px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addNewMilestone}
                                disabled={!newMilestone.name}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    backgroundColor: newMilestone.name ? '#10b981' : '#9ca3af',
                                    color: 'white',
                                    cursor: newMilestone.name ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {editingMilestone ? 'Update Milestone' : 'Add Milestone'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedDashboard;
